import { Suspense, useCallback, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { Icon } from "@zapier/design-system";
import { Button } from "@zapier/design-system-beta";
import {
  executeZslAndSaveAtom,
  isExecutingZslAtom,
  upgradeBannerPropsAsyncAtom,
  emitEventOnAIExecutionAtom,
  dismissFeedbackFormCommand,
  emitAiPredictionUserFeedbackEventAtom,
  zslFeedbackPredictionIdAtom,
  isAiPageAtom,
  numberOfUniqueTableNodesAtom,
  numberOfUniqueChatbotNodesAtom,
  numberOfUniqueInterfaceNodesAtom,
} from "@zapier/canvas-state";
import {
  aiPromptAtom,
  handleModifyPromptCommand,
  startOverCommand,
  tuningPromptsAtom,
} from "@zapier/streaming";

import { ProgressBanner } from "../ProgressBanner";
import { BannerWrapperV2 } from "../BannerWrapperV2";
import { IMPLICIT_GOOD } from "@zapier/canvas-constants";

import styles from "./AiCopilotBanner.module.css";
import { UpgradeButton } from "../UpgradeButton";

type Props = {
  handleModifyPrompt: () => void;
  handleStartOver: () => void;
  isStartOverButtonLoading: boolean;
};

function TemplateExecutionBanner({
  handleModifyPrompt,
  handleStartOver,
  isStartOverButtonLoading,
}: Props) {
  const executeZslAndSave = useSetAtom(executeZslAndSaveAtom);
  const emitEventOnAIExecution = useSetAtom(emitEventOnAIExecutionAtom);
  const emitImplicitGoodEvent = useSetAtom(
    emitAiPredictionUserFeedbackEventAtom,
  );
  const isZslExecuting = useAtomValue(isExecutingZslAtom);
  const prompt = useAtomValue(aiPromptAtom);
  const dismissFeedbackForm = useSetAtom(dismissFeedbackFormCommand);
  const setTuningPrompts = useSetAtom(tuningPromptsAtom);
  const prediction_id = useAtomValue(zslFeedbackPredictionIdAtom);

  const handleUseTemplate = useCallback(async () => {
    dismissFeedbackForm();
    emitEventOnAIExecution();

    emitImplicitGoodEvent({
      prediction_id,
      user_rating: IMPLICIT_GOOD,
      user_feedback: null,
    });
    setTuningPrompts([]);

    await executeZslAndSave({ prompt });
  }, [
    dismissFeedbackForm,
    emitEventOnAIExecution,
    emitImplicitGoodEvent,
    prediction_id,
    setTuningPrompts,
    executeZslAndSave,
    prompt,
  ]);

  return (
    <BannerWrapperV2>
      <div>
        <Button
          variant="ghost"
          onPress={handleStartOver}
          isDisabled={isZslExecuting}
          isLoading={isStartOverButtonLoading}
        >
          Start over
        </Button>
        <Button
          variant="ghost"
          onPress={handleModifyPrompt}
          isDisabled={isZslExecuting}
        >
          <Icon name="miscAI" size={30} />
          Modify prompt
        </Button>
      </div>
      <Button
        size="medium"
        isLoading={isZslExecuting}
        onPress={handleUseTemplate}
        className={styles.buildButton}
      >
        Build it
      </Button>
    </BannerWrapperV2>
  );
}

function SuspendedBanner({
  handleModifyPrompt,
  handleStartOver,
  isStartOverButtonLoading,
}: Props) {
  const upgradeBannerProps = useAtomValue(upgradeBannerPropsAsyncAtom);

  if (upgradeBannerProps === null) {
    return (
      <TemplateExecutionBanner
        handleModifyPrompt={handleModifyPrompt}
        handleStartOver={handleStartOver}
        isStartOverButtonLoading={isStartOverButtonLoading}
      />
    );
  }

  return (
    <BannerWrapperV2
      alert={
        upgradeBannerProps?.description ||
        "Creating this will put you over your plan’s limit. Upgrade to proceed or modify your prompt to stay within limits."
      }
    >
      <div>
        <Button
          variant="tertiary"
          onPress={handleStartOver}
          isLoading={isStartOverButtonLoading}
        >
          Start over
        </Button>
        <Button variant="tertiary" onPress={handleModifyPrompt}>
          <Icon name="miscAI" size={30} />
          Modify prompt
        </Button>
      </div>
      <UpgradeButton isBold />
    </BannerWrapperV2>
  );
}

export function AiCopilotBanner() {
  const numberOfTableNodes = useAtomValue(numberOfUniqueTableNodesAtom);
  const numberOfInterfaceNodes = useAtomValue(numberOfUniqueInterfaceNodesAtom);
  const numberOfChatbotNodes = useAtomValue(numberOfUniqueChatbotNodesAtom);
  const shouldShowSuspendedBanner =
    numberOfTableNodes + numberOfInterfaceNodes + numberOfChatbotNodes > 0;
  const handleModifyPrompt = useSetAtom(handleModifyPromptCommand);
  const [isStartOverButtonLoading, setIsStartOverButtonLoading] =
    useState(false);

  const handleStartOver = useAtomCallback(
    useCallback(async (get, set) => {
      const isAiPage = get(isAiPageAtom);
      if (isAiPage) {
        /* Show loading state for AI page since it takes a while to create a new canvas
        from AI page which has no real canvas */
        setIsStartOverButtonLoading(true);
      }

      await set(startOverCommand);
    }, []),
  );

  if (shouldShowSuspendedBanner) {
    return (
      <Suspense fallback={<ProgressBanner />}>
        <SuspendedBanner
          handleModifyPrompt={handleModifyPrompt}
          handleStartOver={handleStartOver}
          isStartOverButtonLoading={isStartOverButtonLoading}
        />
      </Suspense>
    );
  }

  return (
    <TemplateExecutionBanner
      handleModifyPrompt={handleModifyPrompt}
      handleStartOver={handleStartOver}
      isStartOverButtonLoading={isStartOverButtonLoading}
    />
  );
}
